/*
 * Copyright (c) Nexign, JSC, 2019-2021.
 */
/**
 * The footer of ExpensesAnalyzerTableAccount component
 *
 * @author Maxim Fedorov
 * @author Evgenij Tyan
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @version 1.16.0
 * @since 1.1.0
 */

import { FC, Fragment, useCallback, useEffect, useState } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { SIDE_BAR_SELECT_ALL_ITEM } from '../../../../components/common/SideBar/SideBarConstants';
import useTableFooterButtons, { IUseTableFooterButtons } from '../../../../components/common/TableFooter/hooks/useTableFooterButtons';
import useTableFooterDisabled from '../../../../components/common/TableFooter/hooks/useTableFooterDisabled';
import {
    TableFooterButtonContainerStyled,
    TableFooterContainerStyled,
    TableFooterCounterContainerStyled,
    TableFooterLeftContainerStyled,
} from '../../../../components/common/TableFooter/styled/TableFooterStyled';
import TableFooterItem from '../../../../components/common/TableFooter/TableFooterItem';
import TableFooterShowMenu from '../../../../components/common/TableFooter/TableFooterShowMenu';
import Basket from '../../../../components/Table/components/footerModal/Basket/components/Basket';
import { ITableReducer } from '../../../../components/Table/reducers/tableReducer';
import {
    makeSelectorTableBasketCount,
    makeSelectorTableButtons,
} from '../../../../components/Table/selectors/tableSelectors';
import { ICheckboxItem, TableFooterNameType, TableNameType } from '../../../../components/Table/types/tableTypes';
import {
    EGTMBasketSectionType,
    EGTMBasketValueType,
} from '../../../../modules/analytics/GoogleTagManager/GTMOperations/GTMBasketOperations';
import { getTranslationFromJson } from '../../../../modules/internationalization/helpers/intGetErrorTrnaslation';
import { makeSelectTranslationPageAnalyzer } from '../../../../modules/internationalization/intSelectors';
import { IPageExpensesAnalyzerTranslation } from '../../../../modules/internationalization/types';
import { logBusinessEvent } from '../../../../modules/logging/loggingActions';
import { ILogBusinessEventPayload } from '../../../../modules/logging/loggingTypes';
import Guard from '../../../../modules/role/Guard';
import { ISessionStorageTimers } from '../../../../modules/storage/storageReducer';
import { makeSelectTimers } from '../../../../modules/storage/storageSelectors';
import { IRootReducer } from '../../../../reducers/rootReducer';
import {
    expensesAnalyzerRedirectCallsDetail,
    expensesAnalyzerRedirectDailyExpenses,
    expensesAnalyzerRedirectSubscribersExpenses,
} from '../../pageExpensesAnalyzerActions';
import {
    makeSelectorExpensesAnalyzerSelectedId,
    makeSelectorExpensesIsTableAccount,
} from '../../pageExpensesAnalyzerSelectors';
import { EExpensesAnalyzerTypes } from '../../types/pageExpensesAnalyzerTypes';

interface IExpensesAnalyzerTableFooterProps {
    isOpen?: boolean;
    loadChart: () => void;
    onClick?: () => void;
    tableCheckedItems?: ICheckboxItem[];
    type: EExpensesAnalyzerTypes;
}

export type TExpensesAnalyzerTableFooter = IExpensesAnalyzerTableFooterProps & IConnectedProps & IDispatchProps;

export const ExpensesAnalyzerTableAccountFooter: FC<TExpensesAnalyzerTableFooter> = props => {
    const { basketTitleAccounts, basketTitleSubscribers } = props.expensesAnalyzerTranslation.tableFooter;
    const [basketTitle, setBasketTitle] = useState<string>(basketTitleAccounts);

    const {
        buttons,
        isTableAccount,
        selectedId,
        expensesAnalyzerTranslation,
    } = props;

    const {
        callDetails,
        showDailyExpenses,
        showOnGraph,
        showSubsExpenses,
        wizardExpensesDetail,
    } = useTableFooterButtons(TableFooterNameType.expensesAnalyzer);

    const {
        isBtnDisabled,
        getFooterButtonTooltipMsg,
    } = useTableFooterDisabled(TableFooterNameType.expensesAnalyzer);

    useEffect(() => {
        if (isTableAccount && selectedId === SIDE_BAR_SELECT_ALL_ITEM) {
            setBasketTitle(basketTitleAccounts);
        } else {
            setBasketTitle(basketTitleSubscribers);
        }
    }, [expensesAnalyzerTranslation, isTableAccount, selectedId]);

    const getTooltipMsg = useCallback((item: IUseTableFooterButtons): string =>
        getFooterButtonTooltipMsg(item.id) || item.tooltipMsg, [getFooterButtonTooltipMsg]);

    const getTooltipJsonMsg = useCallback((item: IUseTableFooterButtons): string => {
        const isAccount = isTableAccount && !selectedId;
        const noSelectedTranslation = getTranslationFromJson(item.tooltipMsg)[isAccount ? 0 : 1];
        return getFooterButtonTooltipMsg(item.id) || noSelectedTranslation;
    }, [isTableAccount, selectedId, getFooterButtonTooltipMsg]);

    const renderBtn = useCallback((
        item: IUseTableFooterButtons,
        getTooltip: (item: IUseTableFooterButtons) => string,
    ) => !!buttons.find(button => button.buttonName === item.id) && (
        <Guard accessKey={item.id}>
            <TableFooterItem
                {...item}
                isDisabled={isBtnDisabled(item.id)}
                tooltipMsg={getTooltip(item)}
            />
        </Guard>
    ), [buttons, isBtnDisabled]);


    return (
        <TableFooterContainerStyled>
            <TableFooterShowMenu
                gtmSectionType={EGTMBasketSectionType.EXPENSES}
                isOpen={!!props.isOpen}
                onClick={props.onClick}
            />

            <Fragment>
                {
                    !!props.countBasket &&
                    <TableFooterCounterContainerStyled className="table-footer__counter">
                        <Basket
                            count={props.countBasket}
                            gtmProps={{
                                sectionType: EGTMBasketSectionType.EXPENSES,
                                valueType: props.selectedId ? EGTMBasketValueType.NUMBER : EGTMBasketValueType.ACCOUNT,
                            }}
                            title={basketTitle}
                        />
                    </TableFooterCounterContainerStyled>
                }
            </Fragment>
            <TableFooterButtonContainerStyled>
                {
                    props.isOpen &&
                    <TableFooterLeftContainerStyled>
                        {renderBtn(showOnGraph, getTooltipJsonMsg)}
                        {renderBtn(callDetails, getTooltipMsg)}
                        {renderBtn(showSubsExpenses, getTooltipJsonMsg)}
                        {renderBtn(showDailyExpenses, getTooltipJsonMsg)}
                        {renderBtn(wizardExpensesDetail, getTooltipMsg)}
                    </TableFooterLeftContainerStyled>
                }
            </TableFooterButtonContainerStyled>
        </TableFooterContainerStyled>
    );
};

interface IConnectedProps {
    buttons: ITableReducer[TableNameType]['buttons'];
    countBasket: number | null;
    isTableAccount: boolean;
    selectedId: number;
    timers: ISessionStorageTimers[];
    expensesAnalyzerTranslation: IPageExpensesAnalyzerTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    buttons: makeSelectorTableButtons(),
    countBasket: makeSelectorTableBasketCount(TableNameType.expensesAnalyzer),
    expensesAnalyzerTranslation: makeSelectTranslationPageAnalyzer(),
    isTableAccount: makeSelectorExpensesIsTableAccount(),
    selectedId: makeSelectorExpensesAnalyzerSelectedId(),
    timers: makeSelectTimers(),
});

interface IDispatchProps {
    logBusinessEvent: (payload: ILogBusinessEventPayload) => void;
    redirectCallsDetail: () => void;
    redirectDailyExpenses: () => void;
    redirectSubscribersExpenses: () => void;
}

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    logBusinessEvent: (payload) => dispatch(logBusinessEvent(payload)),
    redirectCallsDetail: () => dispatch(expensesAnalyzerRedirectCallsDetail()),
    redirectDailyExpenses: () => dispatch(expensesAnalyzerRedirectDailyExpenses()),
    redirectSubscribersExpenses: () => dispatch(expensesAnalyzerRedirectSubscribersExpenses()),
});

export default connect(mapStateToProps, mapDispatchToProps)(ExpensesAnalyzerTableAccountFooter);
